import { useAppDispatch } from '@/hooks';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

import { clearUserProfile, setLoadingPage } from '@/redux/globalReducer';

import { SESSION_STORAGE } from '@/utils/constants';
import { AUTH_BROADCAST_MESSAGE, broadcastLogout } from '@/utils/helpers/broadcastAuth';
import { cleanLocalCache } from '@/utils/helpers/globalHelper';

export interface UseLogoutOptions {
  onBeforeLogout?: () => void | Promise<void>;
}

export const useLogout = (options?: UseLogoutOptions) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const clearLocalSession = useCallback(() => {
    sessionStorage.removeItem('path');
    sessionStorage.removeItem('email');
    sessionStorage.setItem(SESSION_STORAGE.LOGGING_OUT, 'true');
    dispatch(clearUserProfile());
    cleanLocalCache();
  }, [dispatch]);

  const handleMiniWindowLogout = useCallback((localOnly: boolean) => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: AUTH_BROADCAST_MESSAGE.LOGOUT }, window.location.origin);
    } else if (!localOnly) {
      broadcastLogout();
    }
    window.close();
  }, []);

  return useCallback(
    async (localOnly = false) => {
      try {
        const isMiniWindow = Boolean(window.opener) || window.name?.startsWith('mini_');
        if (isMiniWindow) {
          handleMiniWindowLogout(localOnly);
          return;
        }

        dispatch(setLoadingPage(true));
        await options?.onBeforeLogout?.();
        clearLocalSession();

        // Case 1: Passive sync receiver -> only clear local user session
        if (localOnly) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          await auth.removeUser();
          return;
        }

        // Case 2: Active initiator -> broadcast to other tabs and redirect to IdP
        broadcastLogout();

        try {
          await auth.signoutRedirect();
        } catch {
          await auth.signinRedirect();
        }
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        dispatch(setLoadingPage(false));
      }
    },
    [auth, clearLocalSession, dispatch, handleMiniWindowLogout, options]
  );
};

export default useLogout;
